import React from 'react';
import { AlertTriangle } from 'lucide-react';

interface ConfirmationModalProps {
  show: boolean;
  title: string;
  description: string;
  // Optional content under the description, e.g. a preview of what is about to be created.
  children?: React.ReactNode;
  warningTitle?: string;
  warningMessage?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  show,
  title,
  description,
  children,
  warningTitle,
  warningMessage,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  loading = false,
  onConfirm,
  onClose,
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-xl rounded-xl bg-[#1c1c1c] p-6 text-white shadow-2xl border border-gray-700 mx-4"
      >
        <div className="flex items-center justify-between border-b border-gray-700 pb-3 mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>

        <p className="text-sm text-gray-300 mb-4">{description}</p>

        {children && <div className="mb-5 max-h-[55vh] overflow-y-auto">{children}</div>}

        {warningMessage && (
          <div className="flex gap-3 bg-orange-950/40 border border-l-4 border-orange-800 border-l-orange-500 rounded-lg p-3 mb-5">
            <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              {warningTitle && <p className="text-sm font-medium text-orange-400 mb-1">{warningTitle}</p>}
              <p className="text-xs text-orange-200/70 leading-relaxed">{warningMessage}</p>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="px-5 py-2 bg-red-700 hover:bg-red-800 rounded-full font-medium transition-all text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
